//************************************************************************ /
// COMMUNITY GOAL
// Full-width component to be used directly below the header of the Community Board.
// Includes a goal progress bar and a CTA.
//************************************************************************ /

import React from "react";
import Link from "next/link";
import {
  AnimatedProgressBar,
  Button,
  Group,
  Typography,
} from "@dreambigger/design-system/src";
import anim from "@dreambigger/shared/src/components/board/styles/animations.module.scss";
import hover from "../styles/hover.module.scss";
import { useGA } from "@dreambigger/shared/src/hooks";
import { optimizeCloudinaryImg } from "@dreambigger/shared/src/utils";

const { Title, Text } = Typography;

export interface CommunityGoalProps {
  title?: string;
  subtitle?: string;
  ctaText?: string;
  ctaLink?: string;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  totalDonated?: number;
  nextGoal?: string;
  nextGoalText?: string;
  sponsorText?: string;
  sponsorLogo?: string;
}

const CommunityGoal = ({
  title,
  subtitle,
  ctaText = "Donate Now",
  ctaLink,
  secondaryCtaLink,
  secondaryCtaText,
  totalDonated = 0,
  nextGoal = "0",
  nextGoalText,
  sponsorText = "All donations matched by",
  sponsorLogo,
}: CommunityGoalProps) => {
  // Load Analytics
  const googleAnalytics = useGA();

  // Filter the string received from the database to ensure that all $s and commas are removed and convert to int.
  const goalAmount = nextGoal ? parseInt(nextGoal.replace(/\$|,/g, "")) : 0;
  const progressPercent =
    totalDonated !== 0 ? (totalDonated / goalAmount) * 100 : 0;

  const handleCtaClick = () => {
    googleAnalytics.event({
      category: "Card Selection",
      action: "CTA Text:" + ctaText,
      label: "Card Type:Goalbar",
      value: 1,
    });
  };

  return goalAmount > 0 ? (
    <Group
      className={`${anim.enter_f} items-center w-100`}
      style={{
        minHeight: "80px",
        background: "white",
        boxShadow: "rgba(0, 0, 0, 0.16) 0px 2px 2px",
        borderRadius: "0 0 8px 8px",
      }}
    >
      <AnimatedProgressBar height={30} progress={progressPercent} />
      <Group id="header" xs="horizontal" className="w-100 pa-5 sm_pv-7">
        {/* AMOUNT RAISED COLUMN */}
        <Group id="raised" className="w-33 items-start">
          <h3 className="fwb f-3 sm_f-5 mb-0">
            {/* Remove decimals with trunc and then add commas back in with localeString */}
            ${Math.trunc(totalDonated).toLocaleString()}
          </h3>
          <h4 className="ttu fwb f-2 sm_f-3" style={{ color: "gray" }}>
            Raised
          </h4>
        </Group>
        {/* CONTENT COLUMN */}
        <Group id="match" spacing={5} className="w-100 items-center tc">
          {/* Title */}
          <Title className="f-5 md_f-6 mb-3">{title}</Title>
          {/* Subtitle */}
          {subtitle && <Text className="f-3 md_f-4">{subtitle}</Text>}
          {/* Secondary CTA */}
          {secondaryCtaLink && secondaryCtaText && (
            <Link href={secondaryCtaLink} className="primary mb-3 underline">
              {secondaryCtaText || "Learn More"}
            </Link>
          )}
          {/* Button */}
          {ctaLink && (
            <Link href={ctaLink}>
              <Button
                type="primary"
                className={`bg-primary ttu`}
                onClick={handleCtaClick}
              >
                {ctaText}
              </Button>
            </Link>
          )}
        </Group>
        {/* NEXT GOAL COLUMN*/}
        <div
          id="nextGoal"
          className="flex flex-column w-33 items-end justify-between"
        >
          {/* Next Goal */}
          <div className="flex flex-column items-end">
            <h3 className="fwb f-3 sm_f-5 mb-0">
              {/* Use localString to format with commas */}$
              {goalAmount.toLocaleString()}
            </h3>
            <h4 className="ttu fwb f-2 sm_f-3 tr" style={{ color: "gray" }}>
              {nextGoalText}
            </h4>
          </div>
          {/* Sponsored By */}
          <div className="flex flex-column md_flex-row self-end items-center justify-end pt-3 w-100">
            {/* -- Text */}
            <p
              className="f-1 mb-3 gray-7 w-100 tr md_w-40 md_mb-0"
              style={{ lineHeight: "12px" }}
            >
              {sponsorText}
            </p>
            {/* -- Logo */}
            {sponsorLogo && (
              <div className="w-100 md_w-60 md_pl-1">
                <img
                  src={optimizeCloudinaryImg(sponsorLogo)}
                  alt="Sponsor Logo"
                  className="w-100"
                  style={{ objectFit: "contain" }}
                />
              </div>
            )}
          </div>
        </div>
      </Group>
    </Group>
  ) : (
    <></>
  );
};

export default CommunityGoal;
